/** Additional office tiers for recruiting campaign profile intake. */

export const ADDITIONAL_OFFICE_CHECKBOX_KEYS = [
  "additionalOffice",
  "secondAdditionalOffice",
  "thirdAdditionalOffice",
  "fourPlusOffices",
]

export const ADDITIONAL_OFFICE_TIERS = [
  {
    checkboxKey: "additionalOffice",
    checkboxLabel: "Additional Office?",
    detailKeys: [
      "additionalOfficeAddress1",
      "office1IntersectionLandmark",
      "office1ContactName",
      "office1Phonetics",
    ],
    fields: [
      {
        key: "additionalOfficeAddress1",
        label: "Additional Office Address 1",
        multiline: true,
        minRows: 2,
      },
      {
        key: "office1IntersectionLandmark",
        label: "Office 1 Intersection or Landmark",
        multiline: true,
        minRows: 2,
      },
      { key: "office1ContactName", label: "Office 1 Contact Name" },
      { key: "office1Phonetics", label: "Office 1 Phonetics" },
    ],
  },
  {
    checkboxKey: "secondAdditionalOffice",
    checkboxLabel: "2nd Additional Office?",
    requiresKey: "additionalOffice",
    detailKeys: [
      "additionalOfficeAddress2",
      "office2IntersectionLandmark",
      "office2ContactName",
      "office2Phonetics",
    ],
    fields: [
      {
        key: "additionalOfficeAddress2",
        label: "Additional Office Address 2",
        multiline: true,
        minRows: 2,
      },
      {
        key: "office2IntersectionLandmark",
        label: "Office 2 Intersection or Landmark",
        multiline: true,
        minRows: 2,
      },
      { key: "office2ContactName", label: "Office 2 Contact Name" },
      { key: "office2Phonetics", label: "Office 2 Phonetics" },
    ],
  },
  {
    checkboxKey: "thirdAdditionalOffice",
    checkboxLabel: "3rd Additional Office?",
    requiresKey: "secondAdditionalOffice",
    detailKeys: [
      "additionalOfficeAddress3",
      "office3IntersectionLandmark",
      "office3ContactName",
      "office3Phonetics",
    ],
    fields: [
      {
        key: "additionalOfficeAddress3",
        label: "Additional Office Address 3",
        multiline: true,
        minRows: 2,
      },
      {
        key: "office3IntersectionLandmark",
        label: "Office 3 Intersection or Landmark",
        multiline: true,
        minRows: 2,
      },
      { key: "office3ContactName", label: "Office 3 Contact Name" },
      { key: "office3Phonetics", label: "Office 3 Phonetics" },
    ],
  },
]

export const EXTENDED_ADDITIONAL_OFFICES_FIELD = {
  key: "additionalOffices",
  label: "Extended Additional Offices Info",
  multiline: true,
  minRows: 3,
}

export const ALL_ADDITIONAL_OFFICE_DETAIL_KEYS =
  ADDITIONAL_OFFICE_TIERS.flatMap((tier) => tier.detailKeys)

export function isIntakeCheckboxTrue(value) {
  if (typeof value === "boolean") {
    return value
  }
  if (typeof value === "string") {
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase())
  }
  return Boolean(value)
}

/** Clear this tier and all nested tiers when a parent checkbox is turned off. */
export function clearedOfficePatchFromTier(tierIndex) {
  const patch = {}
  for (let i = tierIndex; i < ADDITIONAL_OFFICE_TIERS.length; i += 1) {
    const tier = ADDITIONAL_OFFICE_TIERS[i]
    patch[tier.checkboxKey] = false
    for (const key of tier.detailKeys) {
      patch[key] = ""
    }
  }
  patch.fourPlusOffices = false
  patch.additionalOffices = ""
  return patch
}

export function applyAdditionalOfficePayloadRules(form) {
  const payload = { ...form }
  for (const key of ADDITIONAL_OFFICE_CHECKBOX_KEYS) {
    payload[key] = isIntakeCheckboxTrue(form[key])
  }
  if (!isIntakeCheckboxTrue(payload.additionalOffice)) {
    Object.assign(payload, clearedOfficePatchFromTier(0))
  } else if (!isIntakeCheckboxTrue(payload.secondAdditionalOffice)) {
    Object.assign(payload, clearedOfficePatchFromTier(1))
  } else if (!isIntakeCheckboxTrue(payload.thirdAdditionalOffice)) {
    Object.assign(payload, clearedOfficePatchFromTier(2))
  } else if (!isIntakeCheckboxTrue(payload.fourPlusOffices)) {
    payload.additionalOffices = ""
  }
  return payload
}

export function buildAdditionalOfficeDisplayRows(intake) {
  return buildAdditionalOfficeDisplayBlocks(intake).flatMap((block) => {
    const rows = [[block.checkboxLabel, block.checked ? "Yes" : "No"]]
    if (block.checked && block.detailRows.length) {
      rows.push(...block.detailRows)
    }
    return rows
  })
}

/**
 * Structured blocks for read-only additional-office cards (one panel per tier).
 */
export function buildAdditionalOfficeDisplayBlocks(intake) {
  const blocks = ADDITIONAL_OFFICE_TIERS.map((tier) => {
    const checked = isIntakeCheckboxTrue(intake[tier.checkboxKey])
    const detailRows = checked
      ? tier.fields.map((field) => {
          const value = String(intake[field.key] ?? "").trim()
          return [field.label, value || "—"]
        })
      : []
    return {
      id: tier.checkboxKey,
      checkboxLabel: tier.checkboxLabel,
      checked,
      detailRows,
    }
  })

  const fourPlusChecked = isIntakeCheckboxTrue(intake.fourPlusOffices)
  blocks.push({
    id: "fourPlusOffices",
    checkboxLabel: "4+ Offices?",
    checked: fourPlusChecked,
    detailRows: fourPlusChecked
      ? [
          [
            EXTENDED_ADDITIONAL_OFFICES_FIELD.label,
            String(intake.additionalOffices ?? "").trim() || "—",
          ],
        ]
      : [],
  })

  return blocks
}
